/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  AddCircleOutline,
  RestartAlt,
  CheckCircleOutline,
  Close,
  EditOutlined,
  FlagOutlined,
  HelpOutline,
  Undo,
} from '@mui/icons-material'
import { useMutation, useQuery } from '@apollo/client'
import {
  FLAG_CLASSIFICATION_CROP,
  FLAG_CLASSIFICATION_BATCH,
  SABRE_GET_PRODUCT,
  UltraFlagClassificationCropMutation,
  UltraFlagClassificationCropMutationVariables,
  UltraFlagClassificationBatchMutation,
  UltraFlagClassificationBatchMutationVariables,
  UltraFlaggedTypeChoices,
} from '@dai/graphql'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import useT from '@/i18n/useT'
import { usePageHeaderActions } from '@/layouts/PageHeaderActionsContext'
import type { ReviewMetadata } from '@/graphql'

import { REVIEW_DURATION_MS } from './classificationUtils'
import CopyableIdField from './CopyableIdField'
import CropCard from './CropCard'
import FlagItemModal from './FlagItemModal'
import FullImageDialog from './FullImageDialog'
import ProductDialog from './ProductDialog'
import ProductImageBox from './ProductImageBox'
import ProductSearchAutocomplete from './ProductSearchAutocomplete'
import {
  getProductDisplayName,
  getSearchProductDisplayName,
  ProductByUpcData,
  ProductByUpcVariables,
  SearchProduct,
} from './labelingHelpers'
import {
  makeRecentReplacementFallbackProduct,
  readRecentReplacementProducts,
  rememberRecentReplacementProduct,
} from './recentReplacementProducts'
import {
  formatClassificationRetryAfter,
  useClassificationWorkflow,
} from './useClassificationWorkflow'
import type { ReviewClassificationLocationState } from './review/reviewNavigation'
import ReviewFeedbackNotice from './review/ReviewFeedbackNotice'
import ReviewVerificationChips from './review/ReviewVerificationChips'
import type {
  ClassificationBatch as ClassificationBatchType,
  ClassificationBoundingBox,
} from './classificationTypes'

type ClassificationProps = {
  initialBatch?: ClassificationBatchType | null
  readOnly?: boolean
  reviewMetadata?: ReviewMetadata | null
  onSubmitSuccess?: () => Promise<unknown> | unknown
}

function Classification({
  initialBatch: initialBatchProp = null,
  readOnly = false,
  reviewMetadata: reviewMetadataProp = null,
  onSubmitSuccess,
}: ClassificationProps) {
  const { t } = useT()
  const theme = useTheme()
  const isInlineReplacePanel = useMediaQuery(theme.breakpoints.up('md'))
  const location = useLocation()
  const reviewLocationState =
    (location.state as ReviewClassificationLocationState | null) ?? null
  const initialReviewBatch =
    reviewLocationState?.reviewEditClassificationBatch ?? null
  const reviewMetadata =
    reviewMetadataProp ?? reviewLocationState?.reviewMetadata ?? null
  const initialBatch = initialBatchProp ?? initialReviewBatch
  const {
    acceptedCount,
    batch,
    canSubmit,
    closeSnackbar,
    closeUndoConfirm,
    error,
    handleBatchFlagged,
    handleCropFlagged,
    handleRefresh,
    handleSubmit,
    handleToggleRejected,
    handleUndo,
    leaseLock,
    loading,
    noItems,
    rejectedCount,
    rejectedIds,
    result,
    reviewRemainingMs,
    selectedPreviewBox,
    setSelectedPreviewBoxId,
    snackbarOpen,
    undoConfirmOpen,
    visibleBoxes,
  } = useClassificationWorkflow(initialBatch, {
    manageLeases: !readOnly,
    manageInitialBatchLease: !initialReviewBatch,
    onSubmitSuccess,
  })
  const hasFeedback = Boolean(
    batch?.reviewFeedback || (batch?.returnedFeedbackCount ?? 0) > 0,
  )
  const [feedbackAcknowledged, setFeedbackAcknowledged] = React.useState(false)
  const [feedbackNeedsAttention, setFeedbackNeedsAttention] =
    React.useState(false)

  const [helpOpen, setHelpOpen] = React.useState(false)
  const [flagBatchOpen, setFlagBatchOpen] = React.useState(false)
  const [anchorImageLoaded, setAnchorImageLoaded] = React.useState(false)
  const [anchorImageFailed, setAnchorImageFailed] = React.useState(false)
  const [flagCrop, setFlagCrop] =
    React.useState<ClassificationBoundingBox | null>(null)
  const [replaceDrawerOpen, setReplaceDrawerOpen] = React.useState(false)
  const [selectedAnchorUpc, setSelectedAnchorUpc] = React.useState<
    string | null
  >(initialBatch?.upc ?? null)
  const [selectedReplacementProduct, setSelectedReplacementProduct] =
    React.useState<SearchProduct | null>(null)
  const [productDialogMode, setProductDialogMode] = React.useState<
    'create' | 'edit' | null
  >(null)

  const [flagClassificationCrop] = useMutation<
    UltraFlagClassificationCropMutation,
    UltraFlagClassificationCropMutationVariables
  >(FLAG_CLASSIFICATION_CROP, {
    context: { endPoint: 'mlUltra' },
  })

  const [flagClassificationBatch] = useMutation<
    UltraFlagClassificationBatchMutation,
    UltraFlagClassificationBatchMutationVariables
  >(FLAG_CLASSIFICATION_BATCH, {
    context: { endPoint: 'mlUltra' },
  })

  const originalUpc = batch?.originalUpc ?? batch?.upc ?? null
  const batchResetUpc = batch?.upc ?? null
  const batchKey = React.useMemo(
    () =>
      batch
        ? `${batch.upc}:${batch.boundingBoxes.map(box => box.id).join(',')}`
        : '',
    [batch],
  )
  React.useEffect(() => {
    setFeedbackAcknowledged(false)
    if (hasFeedback) {
      setFeedbackNeedsAttention(true)
      const id = window.setTimeout(() => setFeedbackNeedsAttention(false), 1200)
      return () => window.clearTimeout(id)
    }
  }, [batchKey])

  const requiresAnchorReplacement = Boolean(batch?.requiresAnchorReplacement)
  const effectiveSelectedUpc = selectedAnchorUpc ?? batch?.upc ?? null
  const replacementUpc =
    effectiveSelectedUpc && originalUpc && effectiveSelectedUpc !== originalUpc
      ? effectiveSelectedUpc
      : null
  const anchorWasReplaced = Boolean(
    effectiveSelectedUpc && originalUpc
      ? effectiveSelectedUpc !== originalUpc
      : batch?.anchorWasReplaced,
  )

  const {
    data: productData,
    loading: productLoading,
    refetch: refetchProductByUpc,
  } = useQuery<ProductByUpcData, ProductByUpcVariables>(SABRE_GET_PRODUCT, {
    context: { endPoint: 'sabre' },
    variables: { upc: effectiveSelectedUpc || '' },
    skip: !effectiveSelectedUpc,
  })
  const { data: originalProductData } = useQuery<
    ProductByUpcData,
    ProductByUpcVariables
  >(SABRE_GET_PRODUCT, {
    context: { endPoint: 'sabre' },
    variables: { upc: originalUpc || '' },
    skip: !originalUpc || originalUpc === effectiveSelectedUpc,
  })
  const anchorImageUrl =
    selectedReplacementProduct?.marketingImageUrl ||
    productData?.product?.marketingImageUrl ||
    null
  const activeProductLabel = getProductDisplayName(productData?.product || null)
  const replacementProductLabel = getSearchProductDisplayName(
    selectedReplacementProduct,
  )
  const displayedProductLabel =
    replacementProductLabel ?? activeProductLabel ?? effectiveSelectedUpc
  const originalProductLabel = getProductDisplayName(
    originalProductData?.product || null,
  )
  const editableProduct = productData?.product ?? null

  React.useEffect(() => {
    if (!batchKey) {
      setSelectedAnchorUpc(null)
      setSelectedReplacementProduct(null)
      setReplaceDrawerOpen(false)
      return
    }

    setSelectedAnchorUpc(batchResetUpc)
    setSelectedReplacementProduct(null)
  }, [batchKey, batchResetUpc])

  React.useEffect(() => {
    if (batch?.requiresAnchorReplacement && !readOnly) {
      setReplaceDrawerOpen(true)
    }
  }, [batch?.requiresAnchorReplacement, readOnly])

  React.useEffect(() => {
    if (readOnly && replaceDrawerOpen) {
      setReplaceDrawerOpen(false)
    }
  }, [readOnly, replaceDrawerOpen])

  const handleResetReplacement = React.useCallback(() => {
    setSelectedAnchorUpc(originalUpc)
    setSelectedReplacementProduct(null)
  }, [originalUpc])

  const replaceDrawerContent = (
    <Stack
      spacing={2.5}
      sx={{ width: '100%', p: 2.5, height: '100%', minHeight: 0 }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack spacing={0.5}>
          <Typography variant="h6">
            {t('classification.replaceDrawer.title', 'Replace product')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'classification.replaceDrawer.description',
              'Search for the product these crops actually match.',
            )}
          </Typography>
        </Stack>
        <Button onClick={() => setReplaceDrawerOpen(false)}>
          {t('common.close', 'Close')}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle2">
            {t(
              'classification.replaceDrawer.currentSelection',
              'Current selection',
            )}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {displayedProductLabel ??
              t(
                'classification.replaceDrawer.noSelection',
                'No product selected',
              )}
          </Typography>
          {effectiveSelectedUpc ? (
            <Typography variant="body2" color="text.secondary">
              {`UPC ${effectiveSelectedUpc}`}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={handleResetReplacement}
              disabled={!replacementUpc}
            >
              {t('classification.replaceDrawer.reset', 'Reset to original')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              onClick={() => setProductDialogMode('create')}
            >
              {t('classification.actions.createProduct', 'Create product')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditOutlined />}
              onClick={() => setProductDialogMode('edit')}
              disabled={!editableProduct}
            >
              {t('classification.actions.editProduct', 'Edit product')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <ProductSearchAutocomplete
        value={selectedReplacementProduct}
        fullWidth
        minWidth={0}
        label={t('classification.replaceDrawer.searchLabel', 'Search products')}
        placeholder={t(
          'classification.replaceDrawer.searchPlaceholder',
          'Try a UPC, preferred name, or brand',
        )}
        onChange={nextValue => {
          setSelectedAnchorUpc(nextValue?.upcA ?? originalUpc)
          setSelectedReplacementProduct(nextValue)
        }}
      />
    </Stack>
  )

  const handleProductSaved = React.useCallback(
    async (savedUpc: string) => {
      setSelectedAnchorUpc(savedUpc)
      setSelectedReplacementProduct(null)
      rememberRecentReplacementProduct(
        readRecentReplacementProducts(),
        makeRecentReplacementFallbackProduct(savedUpc),
      )
      await refetchProductByUpc({ upc: savedUpc })
    },
    [refetchProductByUpc],
  )

  const pageHeaderActions = React.useMemo(
    () => (
      <Tooltip
        title={t('classification.help.tooltip', 'How classification works')}
      >
        <IconButton
          size="small"
          onClick={() => setHelpOpen(true)}
          aria-label={t(
            'classification.help.ariaLabel',
            'Open classification help',
          )}
        >
          <HelpOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
    [t],
  )
  usePageHeaderActions(pageHeaderActions)

  return (
    <>
      <Helmet title={t('classification.helmet', 'Classification')} />
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ md: 'stretch' }}
        >
          <Box
            sx={theme => ({
              flex: '1 1 0%',
              border: '1px solid',
              borderColor: theme.palette.divider,
              borderRadius: 2,
              p: 2,
              backgroundColor: theme.palette.background.paper,
            })}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
            >
              {batch?.upc ? (
                <Box
                  sx={{
                    width: 104,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignSelf: 'center',
                  }}
                >
                  {productLoading ? (
                    <Skeleton
                      variant="rounded"
                      width={104}
                      height={104}
                      sx={{ borderRadius: 2 }}
                    />
                  ) : null}
                  {!productLoading ? (
                    <ProductImageBox
                      imageUrl={anchorImageUrl}
                      size={104}
                      alt={t('classification.anchorImage.alt', {
                        upc: batch?.upc ?? '',
                        defaultValue: `Product anchor for ${batch?.upc ?? ''}`,
                      })}
                      noImageLabel={t(
                        'classification.anchorImage.unavailable',
                        'No image',
                      )}
                      padding={0.75}
                      previewImageSize={420}
                    />
                  ) : null}
                </Box>
              ) : null}
              <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                {batch && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={t('classification.summary.upc', {
                        upc: effectiveSelectedUpc ?? '',
                        defaultValue: `UPC ${effectiveSelectedUpc ?? ''}`,
                      })}
                      color="primary"
                    />
                    {!readOnly ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditOutlined />}
                        onClick={() => setReplaceDrawerOpen(true)}
                      >
                        {t(
                          'classification.actions.replaceProduct',
                          'Replace product',
                        )}
                      </Button>
                    ) : null}
                    {anchorWasReplaced && originalUpc ? (
                      <Chip
                        label={t('classification.summary.originalUpc', {
                          upc: originalUpc,
                          defaultValue: `Original ${originalUpc}`,
                        })}
                        variant="outlined"
                      />
                    ) : null}
                    {readOnly && reviewMetadata ? (
                      <ReviewVerificationChips
                        isVerified={reviewMetadata.isVerified}
                        verifiedByEmail={reviewMetadata.verifiedByEmail}
                        verifiedAt={reviewMetadata.verifiedAt}
                      />
                    ) : null}
                  </Stack>
                )}
                {displayedProductLabel ? (
                  <Typography variant="h5" sx={{ maxWidth: 760 }}>
                    {displayedProductLabel}
                  </Typography>
                ) : batch ? (
                  <Typography variant="h5">{effectiveSelectedUpc}</Typography>
                ) : null}
                {productData?.product?.name && !selectedReplacementProduct && (
                  <Typography variant="body2" color="text.secondary">
                    {productData.product.name}
                  </Typography>
                )}
                {requiresAnchorReplacement && !readOnly && !replacementUpc ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {t(
                      'classification.summary.requiresAnchorReplacement',
                      'This is a low-confidence batch. Replace with a known product before submitting.',
                    )}
                  </Alert>
                ) : null}
                {anchorWasReplaced && originalUpc ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('classification.summary.replacedAnchor', {
                      label: originalProductLabel ?? originalUpc,
                      defaultValue: `Anchor changed from ${originalProductLabel ?? originalUpc}.`,
                    })}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Box>
          <Stack
            spacing={1.5}
            sx={{
              width: { xs: '100%', md: 380 },
              flexShrink: 0,
            }}
          >
            {batch && (
              <Box
                sx={theme => ({
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: theme.palette.background.paper,
                })}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1.5}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip
                        label={t('classification.summary.accepted', {
                          count: acceptedCount,
                          defaultValue: `${acceptedCount} accepted`,
                        })}
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        label={t('classification.summary.rejected', {
                          count: rejectedCount,
                          defaultValue: `${rejectedCount} rejected`,
                        })}
                        color="error"
                        variant="outlined"
                      />
                    </Stack>
                    {readOnly && reviewMetadata?.objectId ? (
                      <Box
                        sx={theme => ({
                          minWidth: 0,
                          maxWidth: 176,
                          border: '1px solid',
                          borderColor: theme.palette.divider,
                          borderRadius: 1.5,
                          px: 1.25,
                          py: 0.875,
                          backgroundColor: theme.palette.background.default,
                          flexShrink: 0,
                        })}
                      >
                        <CopyableIdField
                          label={t(
                            'classification.summary.batchId',
                            'Batch ID',
                          )}
                          value={reviewMetadata.objectId}
                          inline
                          maxWidth={160}
                        />
                      </Box>
                    ) : null}
                  </Stack>
                  <Stack sx={{ width: '100%' }} spacing={0.75}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'classification.summary.reviewTimer',
                          'Review timer',
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {reviewRemainingMs === 0
                          ? t('classification.summary.ready', 'Ready')
                          : `${(reviewRemainingMs / 1000).toFixed(1)}s`}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={
                        100 - (reviewRemainingMs / REVIEW_DURATION_MS) * 100
                      }
                    />
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button
                      variant="outlined"
                      startIcon={<FlagOutlined />}
                      onClick={() => setFlagBatchOpen(true)}
                      disabled={!visibleBoxes.length || readOnly}
                    >
                      {t('classification.actions.flagBatch', 'Flag batch')}
                    </Button>
                    <Button
                      id="classification-submit"
                      variant="contained"
                      startIcon={<CheckCircleOutline />}
                      onClick={() => void handleSubmit(replacementUpc)}
                      disabled={
                        !canSubmit ||
                        readOnly ||
                        (requiresAnchorReplacement && !replacementUpc) ||
                        (hasFeedback && !feedbackAcknowledged)
                      }
                    >
                      {t('classification.actions.submit', 'Submit')}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Stack>
        </Stack>

        {loading && !result && (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error.message}</Alert>}

        {leaseLock && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                {t('common.retry', 'Retry')}
              </Button>
            }
          >
            {t('classification.leaseLock.message', {
              message: leaseLock.message,
              retryAfter: formatClassificationRetryAfter(
                leaseLock.retryAfterMs,
                t,
              ),
              defaultValue: `${leaseLock.message} Retry in ${formatClassificationRetryAfter(leaseLock.retryAfterMs, t)}.`,
            })}
          </Alert>
        )}

        {noItems && <Alert severity="info">{noItems.message}</Alert>}

        <ReviewFeedbackNotice
          feedback={batch?.reviewFeedback}
          returnedCount={batch?.returnedFeedbackCount}
          onAcknowledge={
            !readOnly && hasFeedback
              ? () => setFeedbackAcknowledged(true)
              : undefined
          }
          acknowledged={feedbackAcknowledged}
          needsAttention={feedbackNeedsAttention}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {batch && (
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns:
                    'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
                  alignItems: 'start',
                }}
              >
                {batch.boundingBoxes.map(box => {
                  const rejected = rejectedIds.has(box.id)
                  return (
                    <Box key={box.id}>
                      <CropCard
                        box={box}
                        rejected={rejected}
                        readOnly={readOnly}
                        onFlag={() => setFlagCrop(box)}
                        onOpenPreview={() => setSelectedPreviewBoxId(box.id)}
                        onToggleRejected={() =>
                          readOnly ? undefined : handleToggleRejected(box.id)
                        }
                      />
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
          {isInlineReplacePanel && replaceDrawerOpen && !readOnly ? (
            <Paper
              variant="outlined"
              sx={{
                width: 500,
                flexShrink: 0,
                position: 'sticky',
                top: 88,
              }}
            >
              {replaceDrawerContent}
            </Paper>
          ) : null}
        </Box>
      </Stack>
      <FullImageDialog
        box={selectedPreviewBox}
        open={!!selectedPreviewBox}
        onClose={() => setSelectedPreviewBoxId(null)}
        selectedUpc={effectiveSelectedUpc}
      />
      <Dialog
        open={!isInlineReplacePanel && replaceDrawerOpen && !readOnly}
        onClose={() => setReplaceDrawerOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ p: 0 }}>{replaceDrawerContent}</DialogContent>
      </Dialog>
      <FlagItemModal
        open={Boolean(flagCrop)}
        onClose={() => setFlagCrop(null)}
        flagType={UltraFlaggedTypeChoices.CLASSIFICATION_CROP}
        title={t('classification.flagCrop.title', 'Flag Classification Crop')}
        subjectLabel={t(
          'classification.flagCrop.subjectLabel',
          'classification crop',
        )}
        onSubmit={async ({ reasonOptionId, moreInfo }) => {
          if (!flagCrop) return

          try {
            const response = await flagClassificationCrop({
              variables: {
                input: {
                  boxId: flagCrop.id,
                  reasonOptionId,
                  moreInfo: moreInfo ?? null,
                },
              },
            })
            const payload = response.data?.flagClassificationCrop

            if (payload?.__typename !== 'ClassificationFlagCropResult') {
              throw new Error(
                t(
                  'classification.flagCrop.failure',
                  'Flag classification crop did not succeed',
                ),
              )
            }

            await handleCropFlagged(payload.boundingBox.id)
            setFlagCrop(null)
          } catch (error) {
            console.error(
              'Failed to finish flagging classification crop',
              error,
            )
            throw error
          }
        }}
      />
      <FlagItemModal
        open={flagBatchOpen}
        onClose={() => setFlagBatchOpen(false)}
        flagType={UltraFlaggedTypeChoices.CLASSIFICATION_CROP}
        title={t('classification.flagBatch.title', 'Flag Classification Batch')}
        subjectLabel={t(
          'classification.flagBatch.subjectLabel',
          'classification batch',
        )}
        onSubmit={async ({ reasonOptionId, moreInfo }) => {
          const boxIds = visibleBoxes.map(box => box.id)
          if (!boxIds.length) return

          const response = await flagClassificationBatch({
            variables: {
              input: {
                boxIds,
                reasonOptionId,
                moreInfo: moreInfo ?? null,
              },
            },
          })
          const payload = response.data?.flagClassificationBatch

          if (
            payload?.__typename !== 'ClassificationFlagBatchResult' ||
            !payload.success
          ) {
            throw new Error(
              t(
                'classification.flagBatch.failure',
                'Flag classification batch did not succeed',
              ),
            )
          }

          setFlagBatchOpen(false)
          await handleBatchFlagged(boxIds)
        }}
      />
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t('classification.helpDialog.title', 'How Classification Works')}
          <IconButton
            aria-label={t(
              'classification.helpDialog.close.ariaLabel',
              'Close classification help',
            )}
            onClick={() => setHelpOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t(
                  'classification.helpDialog.instructions.title',
                  'Instructions',
                )}
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  {t(
                    'classification.helpDialog.instructions.batch',
                    'Each batch contains crops that currently share the same UPC. Review the group and remove anything that does not match the product shown above.',
                  )}
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  {t(
                    'classification.helpDialog.instructions.replaceProduct',
                    'If most of the crops match a different product, replace the top product first, then accept anything that matches the new product.',
                  )}
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  {t(
                    'classification.helpDialog.instructions.toggleCrop',
                    'Click a crop to toggle it between accepted and rejected.',
                  )}
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  {t(
                    'classification.helpDialog.instructions.submitBatch',
                    'Submit the batch once only matching products remain.',
                  )}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('classification.helpDialog.controls.title', 'Controls')}
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  {t(
                    'classification.helpDialog.controls.expandCrop',
                    'Use the expand icon on any crop to inspect the full source image with the target product highlighted.',
                  )}
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  {t(
                    'classification.helpDialog.controls.rejectedCrops',
                    'Rejected crops stay in the classification workflow and can be reassigned to a different label later.',
                  )}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('classification.helpDialog.flagging.title', 'Flagging')}
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  {t(
                    'classification.helpDialog.flagging.batch',
                    'Flag the batch when the visible crops should leave the active workflow, such as when submission fails or the whole group has the same problem.',
                  )}
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  {t(
                    'classification.helpDialog.flagging.rejectionVsFlagging',
                    'Use rejection when a crop should stay in classification under a different label, and use flagging when it should leave the active queue altogether.',
                  )}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={10000}
        onClose={closeSnackbar}
        message={t('classification.snackbar.batchSubmitted', 'Batch submitted')}
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<Undo />}
            onClick={handleUndo}
          >
            {t('common.undo', 'Undo')}
          </Button>
        }
      />
      <Snackbar
        open={undoConfirmOpen}
        autoHideDuration={5000}
        onClose={closeUndoConfirm}
        message={t(
          'classification.snackbar.batchReturned',
          'Batch has been unverified and returned to the queue',
        )}
      />
      <ProductDialog
        mode={productDialogMode ?? 'create'}
        open={Boolean(productDialogMode)}
        initialProduct={productDialogMode === 'edit' ? editableProduct : null}
        onClose={() => setProductDialogMode(null)}
        onSaved={handleProductSaved}
      />
    </>
  )
}

export default Classification
